import { WebLocale } from '@/enums/web-locale';
import { PageType } from '@/routing/config';
import { RouteConfig } from 'vue-router';

export function makeUserRoutes(currentLocale: WebLocale): RouteConfig[] {
	const routes: RouteConfig[] = [];

	if (isAuthorLocale(currentLocale)) routes.push(makeAuthorRoute(currentLocale));
	if (isPersonLocale(currentLocale)) routes.push(makePersonRoute(currentLocale));
	if (isProfileLocale(currentLocale)) routes.push(makeProfileRoute(currentLocale));

	// LEGACY REDIRECTS
	// Before user profiles we had a custom landing page for David Hain.
	routes.push(
		{
			path: '/li/DavidHain',
			redirect: '/de/autor/david-hain-behaind',
		},

		{
			path: '/de/DavidHain',
			redirect: '/de/autor/david-hain-behaind',
		}
	);

	return routes;
}

/////////////
/* AUTHORS */
/////////////
type AuthorLocale = keyof typeof authorSlugs;

const authorSlugs = {
	[WebLocale.ar]: 'autor',
	[WebLocale.au]: 'author',
	[WebLocale.br]: 'autor',
	[WebLocale.ca]: 'author',
	[WebLocale.fr]: 'auteur',
	[WebLocale.de]: 'autor',
	[WebLocale.it]: 'autore',
	[WebLocale.mx]: 'autor',
	[WebLocale.nz]: 'author',
	[WebLocale.pt]: 'autor',
	[WebLocale.es]: 'autor',
	[WebLocale.uk]: 'author',
	[WebLocale.us]: 'author',
	[WebLocale.bs]: 'author',
	[WebLocale.in]: 'author',
	[WebLocale.tr]: 'yazar',
} as const;

export function makeAuthorRoute(currentLocale: AuthorLocale): RouteConfig {
	return {
		path: `/${currentLocale}/${authorSlugs[currentLocale]}/:name`,
		name: 'app.user.author',
		component: () => import(/* webpackChunkName: "userprofile" */ '@/pages/UserProfile.vue'),
		meta: {
			tab: 'user-profile',
			hasFilterBar: false,
			pageType: PageType.USER_PROFILE,
		},
	};
}

function isAuthorLocale(locale: WebLocale): locale is AuthorLocale {
	return locale in authorSlugs;
}

/////////////
/* PEOPLE */
/////////////
type PersonLocale = keyof typeof personSlugs;

const personSlugs = {
	[WebLocale.ar]: 'personas',
	[WebLocale.au]: 'people',
	[WebLocale.br]: 'pessoas',
	[WebLocale.ca]: 'people',
	[WebLocale.fr]: 'personnes',
	[WebLocale.de]: 'personen',
	[WebLocale.it]: 'persone',
	[WebLocale.mx]: 'personas',
	[WebLocale.nz]: 'people',
	[WebLocale.pt]: 'pessoas',
	[WebLocale.es]: 'personas',
	[WebLocale.uk]: 'people',
	[WebLocale.us]: 'people',
	[WebLocale.bs]: 'people',
	[WebLocale.in]: 'people',
	[WebLocale.tr]: 'insanlar',
} as const;

export function makePersonRoute(currentLocale: PersonLocale): RouteConfig {
	return {
		path: `/${currentLocale}/${personSlugs[currentLocale]}/:name`,
		name: 'app.user.person',
		component: () => import(/* webpackChunkName: "userprofile" */ '@/pages/UserProfile.vue'),
		meta: {
			tab: 'user-profile',
			hasFilterBar: false,
			pageType: PageType.USER_PROFILE,
		},
	};
}

function isPersonLocale(locale: WebLocale): locale is PersonLocale {
	return locale in personSlugs;
}

/////////////
/* PROFILES */
/////////////
type ProfileLocale = keyof typeof profileSlugs;

const profileSlugs = {
	[WebLocale.ar]: 'perfil',
	[WebLocale.au]: 'profile',
	[WebLocale.br]: 'perfil',
	[WebLocale.ca]: 'profile',
	[WebLocale.fr]: 'profil',
	[WebLocale.de]: 'profil',
	[WebLocale.it]: 'profilo',
	[WebLocale.mx]: 'perfil',
	[WebLocale.nz]: 'profile',
	[WebLocale.pt]: 'perfil',
	[WebLocale.es]: 'perfil',
	[WebLocale.uk]: 'profile',
	[WebLocale.us]: 'profile',
	[WebLocale.bs]: 'profile',
	[WebLocale.in]: 'profile',
	[WebLocale.tr]: 'profil',
} as const;

export function makeProfileRoute(currentLocale: ProfileLocale): RouteConfig {
	return {
		path: `/${currentLocale}/${profileSlugs[currentLocale]}/:name`,
		name: 'app.user.profile',
		component: () => import(/* webpackChunkName: "userprofile" */ '@/pages/UserProfile.vue'),
		meta: {
			tab: 'user-profile',
			hasFilterBar: false,
			pageType: PageType.USER_PROFILE,
		},
	};
}

function isProfileLocale(locale: WebLocale): locale is ProfileLocale {
	return locale in profileSlugs;
}
